
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Loader2, RefreshCw, ArrowLeft, Download, ArrowUpDown, Settings, Check, FilterX } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

export default function SuppliersAnalysis() {
    const [tables, setTables] = useState([]);
    const [table1, setTable1] = useState('');
    const [table2, setTable2] = useState('');
    const [loading, setLoading] = useState(false);

    // Data States
    const [articlesData, setArticlesData] = useState(null);
    const [suppliersTurnoverData, setSuppliersTurnoverData] = useState(null);

    useEffect(() => {
        fetchTables();
    }, []);

    const fetchTables = async () => {
        try {
            const res = await axios.get(`${API}/tables`);
            setTables(res.data || []);
        } catch (e) {
            console.error(e);
            toast.error("Nu am putut incarca lista de fisiere.");
        }
    };

    const handleCompare = async () => {
        if (!table1 || !table2) {
            toast.warning("Te rog selecteaza ambele perioade.");
            return;
        }
        setLoading(true);
        setArticlesData(null);
        setSuppliersTurnoverData(null);

        try {
            // Parallel Fetch
            const [artRes, suppRes] = await Promise.all([
                axios.post(`${API}/reports/articles-comparison-analysis`, { table1, table2 }),
                axios.post(`${API}/reports/suppliers-clients-analysis`, { table1, table2 })
            ]);

            if (artRes.data.error) throw new Error(artRes.data.error);
            if (suppRes.data.error) throw new Error(suppRes.data.error);

            setArticlesData(artRes.data);
            setSuppliersTurnoverData(suppRes.data);

            toast.success("Toate rapoartele incarcate cu succes!");
        } catch (e) {
            console.error(e);
            toast.error("Eroare la incarcarea datelor.");
        } finally {
            setLoading(false);
        }
    };

    const p1Label = getYearLabel(table1) || 'P1';
    const p2Label = getYearLabel(table2) || 'P2';

    return (
        <div className="p-8 space-y-8 pb-40">
            {/* Header */}
            <div className="flex items-center space-x-4">
                <Link to="/reports">
                    <Button variant="ghost" className="text-gray-400 hover:text-white">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Inapoi la Rapoarte
                    </Button>
                </Link>
                <div className="flex-1">
                    <h1 className="text-3xl font-bold text-white">Analiza Furnizori / Articole</h1>
                </div>
            </div>

            {/* Selection Card */}
            <Card className="bg-[#18181b] border-white/10">
                <CardHeader>
                    <CardTitle className="text-gray-300 text-lg">Selectie Perioade de Comparatie</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                    <div className="space-y-2">
                        <label className="text-sm text-gray-400">Perioada 1 (Ex: 2024)</label>
                        <Select value={table1} onValueChange={setTable1}>
                            <SelectTrigger className="bg-[#27272a] border-white/20 text-white"><SelectValue placeholder="Selecteaza fisier..." /></SelectTrigger>
                            <SelectContent className="bg-[#27272a] border-white/20 text-white h-[300px]">
                                {tables.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm text-gray-400">Perioada 2 (Ex: 2025)</label>
                        <Select value={table2} onValueChange={setTable2}>
                            <SelectTrigger className="bg-[#27272a] border-white/20 text-white"><SelectValue placeholder="Selecteaza fisier..." /></SelectTrigger>
                            <SelectContent className="bg-[#27272a] border-white/20 text-white h-[300px]">
                                {tables.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button onClick={handleCompare} disabled={loading || !table1 || !table2} className="bg-red-600 hover:bg-red-700 text-white">
                        {loading ? <Loader2 className="animate-spin mr-2" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                        Genereaza Raport
                    </Button>
                </CardContent>
            </Card>

            {/* Articles Table */}
            {articlesData && (
                <DetailedArticlesTable
                    title="Cantitate vanduta pe articol (2024 vs 2025)"
                    data={articlesData}
                    p1Label={p1Label}
                    p2Label={p2Label}
                />
            )}

            {/* Suppliers Turnover Table */}
            {suppliersTurnoverData && (
                <SuppliersTurnoverTable
                    title="CA Furnizori"
                    data={suppliersTurnoverData}
                    p1Label={p1Label}
                    p2Label={p2Label}
                />
            )}
        </div>
    );
}

// --- DETAILED ARTICLES TABLE (Components reused or separated would be cleaner but keeping in one file for speed) ---
function DetailedArticlesTable({ title, data, p1Label, p2Label }) {
    const [sortConfig, setSortConfig] = useState({ key: 'sales_2025', direction: 'desc' });
    const [filters, setFilters] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 15;

    // Fixed Column Config
    const allColumns = [
        { key: 'code', label: 'Cod', group: 'Articol' },
        { key: 'name', label: 'Nume Articol', group: 'Articol' },
        { key: 'qty_2024', label: 'Qty ' + p1Label, group: 'Cantitati' },
        { key: 'qty_2025', label: 'Qty ' + p2Label, group: 'Cantitati' },
        { key: 'qty_diff', label: 'Qty Diff', group: 'Cantitati' },
        { key: 'qty_growth', label: 'Qty %', group: 'Cantitati' },
        { key: 'sales_2024', label: 'Sales ' + p1Label, group: 'Vanzari' },
        { key: 'sales_2025', label: 'Sales ' + p2Label, group: 'Vanzari' },
        { key: 'sales_diff', label: 'Sales Diff', group: 'Vanzari' },
        { key: 'sales_growth', label: 'Sales %', group: 'Vanzari' },
        { key: 'profit_2024', label: 'Profit ' + p1Label, group: 'Profit' },
        { key: 'profit_2025', label: 'Profit ' + p2Label, group: 'Profit' },
        { key: 'profit_diff', label: 'Profit Diff', group: 'Profit' },
        { key: 'profit_growth', label: 'Profit %', group: 'Profit' },
        { key: 'supplier', label: 'Furnizor', group: 'Furnizor' },
    ];

    const [visibleColumns, setVisibleColumns] = useState(
        allColumns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {})
    );

    useEffect(() => setCurrentPage(1), [filters, sortConfig, data]);

    const formatMoney = (val) => val ? Number(val).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
    const formatQty = (val) => val ? Number(val).toLocaleString('ro-RO', { maximumFractionDigits: 0 }) : '0';
    const formatPct = (val) => val ? `${(val * 100).toFixed(0)}%` : '0%';
    const toggleColumn = (key) => setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }));

    const processedData = useMemo(() => {
        if (!data) return [];
        let items = [...data];
        if (Object.keys(filters).length > 0) {
            items = items.filter(row => {
                return Object.entries(filters).every(([key, value]) => {
                    if (!value) return true;
                    const cellVal = String(row[key] || '').toLowerCase();
                    return cellVal.includes(value.toLowerCase());
                });
            });
        }
        if (sortConfig.key) {
            items.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];
                if (typeof aVal === 'string') aVal = aVal.toLowerCase();
                if (typeof bVal === 'string') bVal = bVal.toLowerCase();
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return items;
    }, [data, filters, sortConfig]);

    const totalPages = Math.ceil(processedData.length / pageSize);
    const paginated = processedData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const totals = useMemo(() => {
        if (!processedData.length) return null;
        const sums = {
            qty_2024: 0, qty_2025: 0,
            sales_2024: 0, sales_2025: 0,
            profit_2024: 0, profit_2025: 0
        };
        processedData.forEach(row => {
            sums.qty_2024 += Number(row.qty_2024) || 0;
            sums.qty_2025 += Number(row.qty_2025) || 0;
            sums.sales_2024 += Number(row.sales_2024) || 0;
            sums.sales_2025 += Number(row.sales_2025) || 0;
            sums.profit_2024 += Number(row.profit_2024) || 0;
            sums.profit_2025 += Number(row.profit_2025) || 0;
        });
        sums.qty_diff = sums.qty_2025 - sums.qty_2024;
        sums.qty_growth = sums.qty_2024 ? (sums.qty_diff / sums.qty_2024) : 0;
        sums.sales_diff = sums.sales_2025 - sums.sales_2024;
        sums.sales_growth = sums.sales_2024 ? (sums.sales_diff / sums.sales_2024) : 0;
        sums.profit_diff = sums.profit_2025 - sums.profit_2024;
        sums.profit_growth = sums.profit_2024 ? (sums.profit_diff / sums.profit_2024) : 0;
        return sums;
    }, [processedData]);

    const handleSort = (key) => setSortConfig(curr => ({ key, direction: curr.key === key && curr.direction === 'asc' ? 'desc' : 'asc' }));

    const handleExport = async () => {
        try {
            const XLSX = await import('xlsx');
            const ws = XLSX.utils.json_to_sheet(processedData.map(row => ({
                "Cod": row.code,
                "Articol": row.name,
                [`Qty ${p1Label}`]: row.qty_2024,
                [`Qty ${p2Label}`]: row.qty_2025,
                [`Sales ${p1Label}`]: row.sales_2024,
                [`Sales ${p2Label}`]: row.sales_2025,
                "Furnizor": row.supplier
            })));
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Articole");
            XLSX.writeFile(wb, "Analiza_Articole.xlsx");
        } catch (e) { toast.error("Export Error"); }
    };

    const isVisible = (k) => visibleColumns[k];
    const getGroupSpan = (keys) => keys.reduce((acc, k) => acc + (isVisible(k) ? 1 : 0), 0);

    const renderHeaderCell = (label, sortKey, width, rowSpan = 1, colSpan = 1) => (
        <TableHead colSpan={colSpan} rowSpan={rowSpan} style={width ? { width } : {}} className="bg-yellow-400 text-black font-extrabold border border-black/20 text-center p-1 cursor-pointer hover:bg-yellow-500/80 h-auto align-middle" onClick={() => sortKey && handleSort(sortKey)}>
            <div className={`flex items-center justify-center gap-1 ${width && width < 100 ? 'text-[10px]' : ''}`}>{label} {sortKey && sortConfig.key === sortKey && <ArrowUpDown className="h-3 w-3" />}</div>
        </TableHead>
    );

    const renderFilterCell = (key) => isVisible(key) && (
        <TableHead className="bg-yellow-400 p-1 border border-black/20">
            <Input className="h-7 text-xs bg-white text-black border-none px-1" value={filters[key] || ''} onChange={e => setFilters(prev => ({ ...prev, [key]: e.target.value }))} />
        </TableHead>
    );

    const renderGrowthBar = (val) => {
        const isPos = val >= 0; const color = isPos ? 'bg-green-500' : 'bg-red-500'; const widthPct = Math.min(100, Math.abs(val * 100));
        return <div className="relative w-full h-6 bg-gray-800 rounded overflow-hidden"><div className={`absolute top-0 left-0 h-full ${color}`} style={{ width: `${widthPct}%` }} /><span className="absolute inset-0 flex items-center justify-end px-1 text-[10px] font-bold text-white mix-blend-difference">{formatPct(val)}</span></div>;
    };

    return (
        <Card className="bg-[#18181b] border-white/10 mt-8 mb-20 shadow-xl">
            <div className="p-4 border-b border-white/10 flex justify-between items-center bg-yellow-400 rounded-t-lg">
                <h3 className="text-lg font-bold text-black">{title}</h3>
                <div className="flex items-center gap-4">
                    <Popover>
                        <PopoverTrigger asChild><Button variant="outline" size="sm" className="bg-black text-yellow-400 border-none h-7 text-xs font-bold"><Settings className="mr-2 h-3 w-3" /> Coloane</Button></PopoverTrigger>
                        <PopoverContent className="w-56 bg-[#18181b] border-white/20 text-white p-0">
                            <div className="p-1 max-h-[300px] overflow-y-auto">{allColumns.map(col => (<div key={col.key} onClick={() => toggleColumn(col.key)} className="flex items-center space-x-2 p-2 hover:bg-white/10 rounded cursor-pointer"><div className={`shrink-0 w-4 h-4 border border-white/40 rounded flex items-center justify-center ${visibleColumns[col.key] ? 'bg-red-600 border-red-600' : ''}`}>{visibleColumns[col.key] && <Check className="h-3 w-3 text-white" />}</div><span className="text-sm">{col.label}</span></div>))}</div>
                        </PopoverContent>
                    </Popover>
                    <Button onClick={handleExport} size="sm" className="bg-black text-yellow-400 border-none h-7"><Download className="mr-2 h-3 w-3" /> Export</Button>
                    <span className="text-sm font-bold text-black border-l border-black/20 pl-4">{processedData.length} intrari</span>
                </div>
            </div>
            <div className="overflow-x-auto">
                <Table className="border-collapse w-full text-xs" style={{ tableLayout: 'fixed', minWidth: '1800px' }}>
                    <TableHeader>
                        <TableRow className="border-none">
                            {getGroupSpan(['code', 'name']) > 0 && renderHeaderCell("Articol", null, null, 1, getGroupSpan(['code', 'name']))}
                            {getGroupSpan(['qty_2024', 'qty_2025', 'qty_diff', 'qty_growth']) > 0 && renderHeaderCell("Cantitati", null, null, 1, getGroupSpan(['qty_2024', 'qty_2025', 'qty_diff', 'qty_growth']))}
                            {getGroupSpan(['sales_2024', 'sales_2025', 'sales_diff', 'sales_growth']) > 0 && renderHeaderCell("Vanzari", null, null, 1, getGroupSpan(['sales_2024', 'sales_2025', 'sales_diff', 'sales_growth']))}
                            {getGroupSpan(['profit_2024', 'profit_2025', 'profit_diff', 'profit_growth']) > 0 && renderHeaderCell("Profit", null, null, 1, getGroupSpan(['profit_2024', 'profit_2025', 'profit_diff', 'profit_growth']))}
                            {getGroupSpan(['supplier']) > 0 && renderHeaderCell("Furnizor", "supplier", 200, 2)}
                        </TableRow>
                        <TableRow className="border-none">
                            {isVisible('code') && renderHeaderCell("Cod", "code", 100)}
                            {isVisible('name') && renderHeaderCell("Nume Articol", "name", 300)}
                            {isVisible('qty_2024') && renderHeaderCell(p1Label, "qty_2024", 80)}
                            {isVisible('qty_2025') && renderHeaderCell(p2Label, "qty_2025", 80)}
                            {isVisible('qty_diff') && renderHeaderCell("Diff", "qty_diff", 80)}
                            {isVisible('qty_growth') && renderHeaderCell("%", "qty_growth", 80)}
                            {isVisible('sales_2024') && renderHeaderCell(p1Label, "sales_2024", 100)}
                            {isVisible('sales_2025') && renderHeaderCell(p2Label, "sales_2025", 100)}
                            {isVisible('sales_diff') && renderHeaderCell("Diff", "sales_diff", 100)}
                            {isVisible('sales_growth') && renderHeaderCell("%", "sales_growth", 80)}
                            {isVisible('profit_2024') && renderHeaderCell(p1Label, "profit_2024", 100)}
                            {isVisible('profit_2025') && renderHeaderCell(p2Label, "profit_2025", 100)}
                            {isVisible('profit_diff') && renderHeaderCell("Diff", "profit_diff", 100)}
                            {isVisible('profit_growth') && renderHeaderCell("%", "profit_growth", 80)}
                            {isVisible('supplier') && renderHeaderCell("Furnizor", "supplier", 200)}
                        </TableRow>
                        <TableRow>
                            {allColumns.map(col => renderFilterCell(col.key))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {paginated.map((row, i) => (
                            <TableRow key={i} className="hover:bg-white/5 border-b border-white/5 text-gray-300">
                                {isVisible('code') && <TableCell className="font-mono text-xs border-r border-white/10">{row.code}</TableCell>}
                                {isVisible('name') && <TableCell className="font-bold text-white border-r border-white/10 truncate" title={row.name}>{row.name}</TableCell>}
                                {isVisible('qty_2024') && <TableCell className="text-right border-r border-white/10">{formatQty(row.qty_2024)}</TableCell>}
                                {isVisible('qty_2025') && <TableCell className="text-right border-r border-white/10 font-bold text-white">{formatQty(row.qty_2025)}</TableCell>}
                                {isVisible('qty_diff') && <TableCell className="text-right border-r border-white/10">{formatQty(row.qty_diff)}</TableCell>}
                                {isVisible('qty_growth') && <TableCell className="border-r border-white/10 p-1">{renderGrowthBar(row.qty_growth)}</TableCell>}
                                {isVisible('sales_2024') && <TableCell className="text-right border-r border-white/10">{formatMoney(row.sales_2024)}</TableCell>}
                                {isVisible('sales_2025') && <TableCell className="text-right border-r border-white/10 font-bold text-white">{formatMoney(row.sales_2025)}</TableCell>}
                                {isVisible('sales_diff') && <TableCell className="text-right border-r border-white/10">{formatMoney(row.sales_diff)}</TableCell>}
                                {isVisible('sales_growth') && <TableCell className="border-r border-white/10 p-1">{renderGrowthBar(row.sales_growth)}</TableCell>}
                                {isVisible('profit_2024') && <TableCell className="text-right border-r border-white/10">{formatMoney(row.profit_2024)}</TableCell>}
                                {isVisible('profit_2025') && <TableCell className="text-right border-r border-white/10 font-bold text-white">{formatMoney(row.profit_2025)}</TableCell>}
                                {isVisible('profit_diff') && <TableCell className="text-right border-r border-white/10">{formatMoney(row.profit_diff)}</TableCell>}
                                {isVisible('profit_growth') && <TableCell className="border-r border-white/10 p-1">{renderGrowthBar(row.profit_growth)}</TableCell>}
                                {isVisible('supplier') && <TableCell className="border-r border-white/10 truncate text-xs text-yellow-500">{row.supplier || '-'}</TableCell>}
                            </TableRow>
                        ))}
                    </TableBody>
                    {totals && (
                        <tfoot className="bg-[#27272a] border-t border-white/20 font-bold text-white sticky bottom-0 z-20">
                            <TableRow className="hover:bg-[#27272a]">
                                {getGroupSpan(['code', 'name']) > 0 && <TableCell className="border-r border-white/10 text-yellow-400 uppercase font-bold pl-2" colSpan={getGroupSpan(['code', 'name'])}>Total</TableCell>}
                                {isVisible('qty_2024') && <TableCell className="text-right border-r border-white/10">{formatQty(totals.qty_2024)}</TableCell>}
                                {isVisible('qty_2025') && <TableCell className="text-right border-r border-white/10 text-yellow-400">{formatQty(totals.qty_2025)}</TableCell>}
                                {isVisible('qty_diff') && <TableCell className="text-right border-r border-white/10">{formatQty(totals.qty_diff)}</TableCell>}
                                {isVisible('qty_growth') && <TableCell className="text-center border-r border-white/10">{formatPct(totals.qty_growth)}</TableCell>}
                                {isVisible('sales_2024') && <TableCell className="text-right border-r border-white/10">{formatMoney(totals.sales_2024)}</TableCell>}
                                {isVisible('sales_2025') && <TableCell className="text-right border-r border-white/10 text-yellow-400">{formatMoney(totals.sales_2025)}</TableCell>}
                                {isVisible('sales_diff') && <TableCell className="text-right border-r border-white/10">{formatMoney(totals.sales_diff)}</TableCell>}
                                {isVisible('sales_growth') && <TableCell className="text-center border-r border-white/10">{formatPct(totals.sales_growth)}</TableCell>}
                                {isVisible('profit_2024') && <TableCell className="text-right border-r border-white/10">{formatMoney(totals.profit_2024)}</TableCell>}
                                {isVisible('profit_2025') && <TableCell className="text-right border-r border-white/10 text-yellow-400">{formatMoney(totals.profit_2025)}</TableCell>}
                                {isVisible('profit_diff') && <TableCell className="text-right border-r border-white/10">{formatMoney(totals.profit_diff)}</TableCell>}
                                {isVisible('profit_growth') && <TableCell className="text-center border-r border-white/10">{formatPct(totals.profit_growth)}</TableCell>}
                                {isVisible('supplier') && <TableCell className="border-r border-white/10"></TableCell>}
                            </TableRow>
                        </tfoot>
                    )}
                </Table>
            </div>
            <div className="flex justify-between items-center p-2 bg-yellow-400 rounded-b-lg">
                <Button size="sm" variant="outline" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="border-black/20 text-black">Anterior</Button>
                <span className="text-black font-bold text-xs">Pagina {currentPage} din {totalPages}</span>
                <Button size="sm" variant="outline" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="border-black/20 text-black">Urmator</Button>
            </div>
        </Card>
    );
}

// --- SUPPLIERS TURNOVER TABLE ---
function SuppliersTurnoverTable({ title, data, p1Label, p2Label }) {
    const [sortConfig, setSortConfig] = useState({ key: 'sales_p2', direction: 'desc' });
    const [filters, setFilters] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 15;

    const allColumns = [
        { key: 'supplier', label: 'Furnizor' },
        { key: 'client', label: 'Client' },
        { key: 'sales_p1', label: 'CA ' + p1Label },
        { key: 'sales_p2', label: 'CA ' + p2Label },
        { key: 'sales_diff', label: 'Diff CA' }, // Virtual
        { key: 'sales_growth', label: 'Crestere CA %' }, // Virtual
        { key: 'profit_p1', label: 'Profit ' + p1Label },
        { key: 'profit_p2', label: 'Profit ' + p2Label },
        { key: 'profit_diff', label: 'Diff Profit' }, // Virtual
        { key: 'profit_growth', label: 'Crestere Profit %' }, // Virtual
        { key: 'share_p2', label: 'Pondere CA ' + p2Label },
        { key: 'sup_clients_count', label: 'Nr Clienti' }
    ];

    const [visibleColumns, setVisibleColumns] = useState(allColumns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {}));

    useEffect(() => setCurrentPage(1), [filters, sortConfig, data]);

    const formatMoney = (val) => val ? Number(val).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
    const formatPct = (val) => val ? `${(val * 100).toFixed(0)}%` : '0%';
    const toggleColumn = (key) => setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }));

    const processedData = useMemo(() => {
        if (!data) return [];
        let items = data.map(row => {
            // Calculate virtual fields if not present
            const s1 = Number(row.sales_p1) || 0;
            const s2 = Number(row.sales_p2) || 0;
            const p1 = Number(row.profit_p1) || 0;
            const p2 = Number(row.profit_p2) || 0;
            return {
                ...row,
                sales_diff: s2 - s1,
                sales_growth: s1 ? (s2 - s1) / s1 : (s2 > 0 ? 1 : 0),
                profit_diff: p2 - p1,
                profit_growth: p1 ? (p2 - p1) / p1 : (p2 > 0 ? 1 : 0)
            };
        });

        if (Object.keys(filters).length > 0) {
            items = items.filter(row => {
                return Object.entries(filters).every(([key, value]) => {
                    if (!value) return true;
                    return String(row[key] || '').toLowerCase().includes(value.toLowerCase());
                });
            });
        }
        if (sortConfig.key) {
            items.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];
                if (typeof aVal === 'string') aVal = aVal.toLowerCase();
                if (typeof bVal === 'string') bVal = bVal.toLowerCase();
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return items;
    }, [data, filters, sortConfig]);

    const totalPages = Math.ceil(processedData.length / pageSize);
    const paginated = processedData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const totals = useMemo(() => {
        if (!processedData.length) return null;
        const sums = {
            sales_p1: 0, sales_p2: 0, profit_p1: 0, profit_p2: 0
        };
        processedData.forEach(row => {
            sums.sales_p1 += Number(row.sales_p1) || 0;
            sums.sales_p2 += Number(row.sales_p2) || 0;
            sums.profit_p1 += Number(row.profit_p1) || 0;
            sums.profit_p2 += Number(row.profit_p2) || 0;
        });
        sums.sales_diff = sums.sales_p2 - sums.sales_p1;
        sums.sales_growth = sums.sales_p1 ? sums.sales_diff / sums.sales_p1 : 0;
        sums.profit_diff = sums.profit_p2 - sums.profit_p1;
        sums.profit_growth = sums.profit_p1 ? sums.profit_diff / sums.profit_p1 : 0;
        return sums;
    }, [processedData]);

    const isVisible = (k) => visibleColumns[k];
    const handleSort = (key) => setSortConfig(curr => ({ key, direction: curr.key === key && curr.direction === 'asc' ? 'desc' : 'asc' }));

    const handleExport = async () => {
        try {
            const XLSX = await import('xlsx');
            const ws = XLSX.utils.json_to_sheet(processedData.map(row => ({
                "Furnizor": row.supplier,
                "Client": row.client,
                [`CA ${p1Label}`]: row.sales_p1,
                [`CA ${p2Label}`]: row.sales_p2,
                "CA Diff": row.sales_diff,
                "CA %": formatPct(row.sales_growth),
                [`Profit ${p1Label}`]: row.profit_p1,
                [`Profit ${p2Label}`]: row.profit_p2,
                "Profit Diff": row.profit_diff,
                "Profit %": formatPct(row.profit_growth),
                "Pondere CA": formatPct(row.share_p2),
                "Nr Clienti": row.sup_clients_count
            })));
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "CA Furnizori");
            XLSX.writeFile(wb, "Analiza_CA_Furnizori.xlsx");
        } catch (e) { toast.error("Export Error"); }
    };

    const renderHeaderCell = (col) => (
        <TableHead key={col.key} style={col.key === 'supplier' || col.key === 'client' ? { minWidth: '200px' } : {}} className="bg-yellow-400 text-black font-extrabold border border-black/20 text-center p-1 cursor-pointer hover:bg-yellow-500/80 h-auto align-middle" onClick={() => handleSort(col.key)}>
            <div className="flex items-center justify-center gap-1">{col.label} {sortConfig.key === col.key && <ArrowUpDown className="h-3 w-3" />}</div>
        </TableHead>
    );
    const renderGrowthBar = (val) => {
        const isPos = val >= 0; const color = isPos ? 'bg-green-500' : 'bg-red-500'; const widthPct = Math.min(100, Math.abs(val * 100));
        return <div className="relative w-full h-6 bg-gray-800 rounded overflow-hidden"><div className={`absolute top-0 left-0 h-full ${color}`} style={{ width: `${widthPct}%` }} /><span className="absolute inset-0 flex items-center justify-end px-1 text-[10px] font-bold text-white mix-blend-difference">{formatPct(val)}</span></div>;
    };

    return (
        <Card className="bg-[#18181b] border-white/10 mt-8 mb-20 shadow-xl">
            <div className="p-4 border-b border-white/10 flex justify-between items-center bg-yellow-400 rounded-t-lg">
                <h3 className="text-lg font-bold text-black">{title}</h3>
                <div className="flex items-center gap-4">
                    <Popover>
                        <PopoverTrigger asChild><Button variant="outline" size="sm" className="bg-black text-yellow-400 border-none h-7 text-xs font-bold"><Settings className="mr-2 h-3 w-3" /> Coloane</Button></PopoverTrigger>
                        <PopoverContent className="w-56 bg-[#18181b] border-white/20 text-white p-0">
                            <div className="p-1 max-h-[300px] overflow-y-auto">{allColumns.map(col => (<div key={col.key} onClick={() => toggleColumn(col.key)} className="flex items-center space-x-2 p-2 hover:bg-white/10 rounded cursor-pointer"><div className={`shrink-0 w-4 h-4 border border-white/40 rounded flex items-center justify-center ${visibleColumns[col.key] ? 'bg-red-600 border-red-600' : ''}`}>{visibleColumns[col.key] && <Check className="h-3 w-3 text-white" />}</div><span className="text-sm">{col.label}</span></div>))}</div>
                        </PopoverContent>
                    </Popover>
                    <Button onClick={handleExport} size="sm" className="bg-black text-yellow-400 border-none h-7"><Download className="mr-2 h-3 w-3" /> Export</Button>
                    <span className="text-sm font-bold text-black border-l border-black/20 pl-4">{processedData.length} intrari</span>
                </div>
            </div>
            <div className="overflow-x-auto">
                <Table className="border-collapse w-full text-xs" style={{ tableLayout: 'fixed', minWidth: '1500px' }}>
                    <TableHeader>
                        <TableRow>
                            {allColumns.map(col => isVisible(col.key) && renderHeaderCell(col))}
                        </TableRow>
                        <TableRow>
                            {allColumns.map(col => isVisible(col.key) && (
                                <TableHead key={col.key} className="bg-yellow-400 p-1 border border-black/20">
                                    <Input className="h-7 text-xs bg-white text-black border-none px-1" value={filters[col.key] || ''} onChange={e => setFilters(prev => ({ ...prev, [col.key]: e.target.value }))} />
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {paginated.map((row, i) => (
                            <TableRow key={i} className="hover:bg-white/5 border-b border-white/5 text-gray-300">
                                {isVisible('supplier') && <TableCell className="font-bold text-yellow-500 border-r border-white/10 truncate" title={row.supplier}>{row.supplier || '-'}</TableCell>}
                                {isVisible('client') && <TableCell className="border-r border-white/10 truncate" title={row.client}>{row.client || '-'}</TableCell>}
                                {isVisible('sales_p1') && <TableCell className="text-right border-r border-white/10">{formatMoney(row.sales_p1)}</TableCell>}
                                {isVisible('sales_p2') && <TableCell className="text-right border-r border-white/10 font-bold text-white">{formatMoney(row.sales_p2)}</TableCell>}
                                {isVisible('sales_diff') && <TableCell className="text-right border-r border-white/10">{formatMoney(row.sales_diff)}</TableCell>}
                                {isVisible('sales_growth') && <TableCell className="border-r border-white/10 p-1">{renderGrowthBar(row.sales_growth)}</TableCell>}
                                {isVisible('profit_p1') && <TableCell className="text-right border-r border-white/10">{formatMoney(row.profit_p1)}</TableCell>}
                                {isVisible('profit_p2') && <TableCell className="text-right border-r border-white/10 font-bold text-white">{formatMoney(row.profit_p2)}</TableCell>}
                                {isVisible('profit_diff') && <TableCell className="text-right border-r border-white/10">{formatMoney(row.profit_diff)}</TableCell>}
                                {isVisible('profit_growth') && <TableCell className="border-r border-white/10 p-1">{renderGrowthBar(row.profit_growth)}</TableCell>}
                                {isVisible('share_p2') && <TableCell className="text-right border-r border-white/10">{formatPct(row.share_p2)}</TableCell>}
                                {isVisible('sup_clients_count') && <TableCell className="text-center border-r border-white/10">{row.sup_clients_count}</TableCell>}
                            </TableRow>
                        ))}
                    </TableBody>
                    {totals && (
                        <tfoot className="bg-[#27272a] border-t border-white/20 font-bold text-white sticky bottom-0 z-20">
                            <TableRow className="hover:bg-[#27272a]">
                                {isVisible('supplier') && <TableCell className="border-r border-white/10 text-yellow-400 uppercase font-bold pl-2">Total</TableCell>}
                                {isVisible('client') && <TableCell className="border-r border-white/10"></TableCell>}
                                {isVisible('sales_p1') && <TableCell className="text-right border-r border-white/10">{formatMoney(totals.sales_p1)}</TableCell>}
                                {isVisible('sales_p2') && <TableCell className="text-right border-r border-white/10 text-yellow-400">{formatMoney(totals.sales_p2)}</TableCell>}
                                {isVisible('sales_diff') && <TableCell className="text-right border-r border-white/10">{formatMoney(totals.sales_diff)}</TableCell>}
                                {isVisible('sales_growth') && <TableCell className="text-center border-r border-white/10">{formatPct(totals.sales_growth)}</TableCell>}
                                {isVisible('profit_p1') && <TableCell className="text-right border-r border-white/10">{formatMoney(totals.profit_p1)}</TableCell>}
                                {isVisible('profit_p2') && <TableCell className="text-right border-r border-white/10 text-yellow-400">{formatMoney(totals.profit_p2)}</TableCell>}
                                {isVisible('profit_diff') && <TableCell className="text-right border-r border-white/10">{formatMoney(totals.profit_diff)}</TableCell>}
                                {isVisible('profit_growth') && <TableCell className="text-center border-r border-white/10">{formatPct(totals.profit_growth)}</TableCell>}
                                {isVisible('share_p2') && <TableCell className="border-r border-white/10"></TableCell>}
                                {isVisible('sup_clients_count') && <TableCell className="border-r border-white/10"></TableCell>}
                            </TableRow>
                        </tfoot>
                    )}
                </Table>
            </div>
            <div className="flex justify-between items-center p-2 bg-yellow-400 rounded-b-lg">
                <Button size="sm" variant="outline" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="border-black/20 text-black">Anterior</Button>
                <span className="text-black font-bold text-xs">Pagina {currentPage} din {totalPages}</span>
                <Button size="sm" variant="outline" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="border-black/20 text-black">Urmator</Button>
            </div>
        </Card>
    );
}

const getYearLabel = (tableName) => {
    if (!tableName) return '';
    const parts = tableName.split(/[_/]/);
    const year = parts.find(p => /^\d{4}$/.test(p));
    return year || tableName;
};
